import * as React from 'react';
import { makeStyles, Select, Slider, TextField } from '@material-ui/core';
import * as styles from './build-segment.module.scss';
import { Clear } from '@material-ui/icons';
import { SegmentAttributeRange, SelectedAttributeRange } from '../../../models/segments';
import { useMinMaxSlider } from './useMinMaxSlider';
import { MultiselectChip } from './multiselect-chip';
import AttributeFilterDialog from './attribute-filter-dialog';
import { blue, neutral } from '../../../theme/colors';
import { useBuildSegment } from './useBuildSegment';

interface Range {
  min: number | null;
  max: number | null;
}

interface Props {
  attribute: SegmentAttributeRange;
  selectedAttribute: SelectedAttributeRange;
  position: number[];
  exclude?: boolean;
  applyTransactional?: (options: any) => void;
  attributeIndex?: number;
}

const useStyles = makeStyles({
  slider: {
    '& .MuiSlider-thumb': {
      color: neutral[300],
      border: `1px solid ${neutral[500]}`
    },
    '& .MuiSlider-track': {
      color: blue[500]
    }
  },
  minmaxInput: {
    maxWidth: 100,
    marginTop: 8,
    marginBottom: 8
  }
});

/**
 * Modal dialog component to render the filter options for a single attribute when building a segment
 */
const AttributeFilterRange = ({
  attribute,
  selectedAttribute,
  position,
  exclude,
  applyTransactional,
  attributeIndex
}: Props) => {
  const overrides = useStyles();
  const { dispatch } = useBuildSegment(exclude);
  const [dialogOpen, setDialogOpen] = React.useState<boolean>(false);
  const [selectedRange, setSelectedRange] = React.useState<Range>({
    min: selectedAttribute.range.min || attribute.filters.min,
    max: selectedAttribute.range.max || attribute.filters.max
  });
  const {
    minValue,
    setMinValue,
    maxValue,
    setMaxValue,
    handleSliderChange,
    handleMinChange,
    handleMinBlur,
    handleMaxChange,
    handleMaxBlur,
    warning
  } = useMinMaxSlider(attribute, selectedAttribute.range);

  React.useEffect(() => {
    setMinValue(selectedRange.min);
    setMaxValue(selectedRange.max);
    const selectedMin = selectedRange.min === attribute.filters.min ? null : selectedRange.min;
    const selectedMax = selectedRange.max === attribute.filters.max ? null : selectedRange.max;
    if (!applyTransactional) {
      dispatch({
        type: 'updateAttribute',
        attribute: { ...selectedAttribute, range: { min: selectedMin, max: selectedMax } },
        position,
        attributeIndex: attributeIndex
      });
    } else {
      applyTransactional({
        min: selectedMin,
        max: selectedMax
      });
    }
  }, [selectedRange]);

  /** Reset to previous filters on cancel */
  const handleCancel = () => {
    setMinValue(selectedRange.min);
    setMaxValue(selectedRange.max);
    setDialogOpen(false);
  };

  const handleApply = () => {
    const nextMin = minValue === '' ? attribute.filters.min : minValue;
    const nextMax = maxValue === '' ? attribute.filters.max : maxValue;
    setSelectedRange({
      // Ensure that min is lower than max, swap if needed
      min: nextMin < nextMax ? nextMin : nextMax,
      max: nextMax > nextMin ? nextMax : nextMin
    });
    setDialogOpen(false);
  };

  /** Function passed to filter select `renderValue` prop: conditionally render the custom content of the filter multi-select menu */
  const renderFilterValues = (value: Range) => {
    if (selectedRange.min == attribute.filters.min && selectedRange.max == attribute.filters.max) {
      return <span className={styles.filterText}>All - click for additional filtering</span>;
    }
    return (
      <div className={styles.filterChips}>
        {value.min != attribute.filters.min && (
          <MultiselectChip
            key={`${attribute.id}-min`}
            label={`Min: ${value.min.toLocaleString()}`}
            size="small"
            deleteIcon={<Clear onMouseDown={(event) => event.stopPropagation()} aria-label="delete filter" />}
            onDelete={() => setSelectedRange((prev) => ({ ...prev, min: attribute.filters.min }))}
          />
        )}
        {value.max != attribute.filters.max && (
          <MultiselectChip
            key={`${attribute.id}-max`}
            label={`Max: ${value.max.toLocaleString()}`}
            size="small"
            deleteIcon={<Clear onMouseDown={(event) => event.stopPropagation()} aria-label="delete filter" />}
            onDelete={() => setSelectedRange((prev) => ({ ...prev, max: attribute.filters.max }))}
          />
        )}
      </div>
    );
  };

  return (
    <React.Fragment>
      <Select
        id={`filter-attribute-${attribute.id}`}
        multiple={true}
        value={selectedRange}
        open={false}
        onOpen={() => {
          setDialogOpen(true);
        }}
        renderValue={renderFilterValues}
        displayEmpty={true}
      />
      <AttributeFilterDialog
        open={dialogOpen}
        title="Select Filters"
        handleCancel={handleCancel}
        handleApply={handleApply}
      >
        <h4 className={styles.rangeFilterTitle}>{attribute.name}</h4>
        <Slider
          value={[
            typeof minValue == 'number' ? minValue : attribute.filters.min,
            typeof maxValue == 'number' ? maxValue : attribute.filters.max
          ]}
          min={attribute.filters.min}
          max={attribute.filters.max}
          onChange={handleSliderChange}
          valueLabelDisplay="off"
          className={overrides.slider}
        />
        <form className={styles.rangeInputGroup}>
          <TextField
            size="small"
            variant="outlined"
            label="Min"
            value={minValue}
            onChange={handleMinChange}
            onBlur={handleMinBlur}
            className={overrides.minmaxInput}
            inputProps={{
              type: 'number',
              min: attribute.filters.min,
              max: attribute.filters.max
            }}
          />
          <span className={styles.rangeInputGroupDash}>-</span>
          <TextField
            size="small"
            variant="outlined"
            label="Max"
            value={maxValue}
            onChange={handleMaxChange}
            onBlur={handleMaxBlur}
            className={overrides.minmaxInput}
            inputProps={{
              type: 'number',
              min: attribute.filters.min,
              max: attribute.filters.max
            }}
          />
        </form>
        {warning && <p className={styles.rangeWarning}>{warning}</p>}
      </AttributeFilterDialog>
    </React.Fragment>
  );
};

export default AttributeFilterRange;
